import { SecureRoute } from '@okta/okta-react';
import React, { FC, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, useRouteMatch } from 'react-router-dom';
import { PageLoading } from 'components/atoms/loading';
import { TemplateUnsavedPrompt } from 'components/organisms/templates/commons/prompt';
import { ProjectDynamicTemplate } from 'components/organisms/templates/projects/dynamic';
import { ProjectDynamicSpacesTemplate } from 'components/organisms/templates/projects/dynamic/spaces/space-dynamic';
import { ProjectTemplateTimeline } from 'components/organisms/templates/projects/gantt';
import { ProjectTemplatePlan } from 'components/organisms/templates/projects/plan';
import { KeyEventPage } from 'components/organisms/templates/projects/plan/key-events/key-event-page';
import { TemplateResourceManagement } from 'components/organisms/templates/projects/resource-management';
import { ProjectTemplateSpaces } from 'components/organisms/templates/projects/spaces';
import { RootState } from 'data';
import {
  createProjectTemplate,
  useActiveProjectTemplate,
} from 'data/templates/projects';
import { useTranslation } from 'hooks/use-translation';
import { ProjectTemplateSpacesRedirect } from './spaces';

export const ProjectTemplateNewRoute: FC = () => {
  const { path } = useRouteMatch();
  const dispatch = useDispatch();
  const template = useActiveProjectTemplate();
  const templateName = useTranslation('untitled');
  const baseTitle = useTranslation('templates.projects.create.title');
  const timeline = useTranslation('projects.plan.timeline');
  const spaces = useTranslation('projects.spaces');
  const resourceManagement = useTranslation('resource_management.title');
  const isProjectTemplate = useSelector(
    ({ templates }: RootState) => templates.project.projectToTemplate,
  );

  useEffect(() => {
    if (!isProjectTemplate) {
      dispatch(
        createProjectTemplate({
          templateName,
        }),
      );
    }
  }, [dispatch, isProjectTemplate, templateName]);

  return !template.templateId ? (
    <PageLoading size="large" />
  ) : (
    <>
      <Helmet>
        <title>{baseTitle}</title>
      </Helmet>
      <TemplateUnsavedPrompt />
      <Switch>
        <SecureRoute path={`${path}/timeline`}>
          <>
            <Helmet>
              <title>{`${baseTitle} | ${timeline}`}</title>
            </Helmet>
            <ProjectTemplateTimeline />
          </>
        </SecureRoute>
        <SecureRoute path={`${path}/spaces/dynamic`}>
          <>
            <Helmet>
              <title>{`${baseTitle} | ${spaces}`}</title>
            </Helmet>
            <ProjectDynamicSpacesTemplate />
          </>
        </SecureRoute>
        <SecureRoute path={`${path}/spaces/:spaceId`}>
          <>
            <Helmet>
              <title>{`${baseTitle} | ${spaces}`}</title>
            </Helmet>
            <ProjectTemplateSpaces />
          </>
        </SecureRoute>
        <SecureRoute path={`${path}/dynamic`}>
          <ProjectDynamicTemplate />
        </SecureRoute>
        <SecureRoute path={`${path}/resource-management`}>
          <>
            <Helmet>
              <title>{`${baseTitle} | ${resourceManagement}`}</title>
            </Helmet>
            <TemplateResourceManagement />
          </>
        </SecureRoute>
        <SecureRoute path={`${path}/spaces`}>
          <ProjectTemplateSpacesRedirect templateId="new" />
        </SecureRoute>
        <SecureRoute path={path}>
          <ProjectTemplatePlan templateId="new" />
          <Switch>
            <SecureRoute exact path={`${path}/automations/:refType/:refId`}>
              <KeyEventPage />
            </SecureRoute>

            <SecureRoute
              exact
              path={`${path}/automations/:automationId/:refType/:refId`}
            >
              <KeyEventPage />
            </SecureRoute>
          </Switch>
        </SecureRoute>
      </Switch>
    </>
  );
};
